// OrdersTab — Order book, trade history, GTT rules
import React, { useState, useEffect, useCallback } from 'react';
import { useTrading } from '../../../context/TradingContext';
import { useOrderManager } from '../../../hooks/useOrderManager';
import { FaTimes, FaSync, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const SUB_TABS = ['Open Orders', 'Executed', 'GTT'];

export default function OrdersTab() {
  const { orderBook, connectionStatus, refreshPortfolio } = useTrading();
  const { cancelExisting, listGTT, cancelGTT, loading, error } = useOrderManager();
  const [subTab, setSubTab] = useState(0);
  const [gttRules, setGttRules] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState(null); // orderid to confirm
  const [toast, setToast] = useState(null); // {type: 'success'|'error', message}
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (connectionStatus === 'connected' && subTab === 2) {
      listGTT().then(setGttRules);
    }
  }, [connectionStatus, subTab, listGTT]);

  // Auto-clear toast after 4 seconds
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refreshPortfolio();
    setTimeout(() => setRefreshing(false), 1000);
  }, [refreshPortfolio]);

  const handleCancel = useCallback(async (order) => {
    const variety = order.variety || 'NORMAL';
    const orderId = order.orderid;
    setCancelConfirm(null);

    try {
      const result = await cancelExisting(orderId, variety);
      if (result?.status === false) {
        setToast({ type: 'error', message: result.message || `Failed to cancel order ${orderId}` });
      } else {
        setToast({ type: 'success', message: `Order ${orderId} cancelled successfully` });
        // Force refresh order book
        setTimeout(() => refreshPortfolio(), 500);
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message || `Failed to cancel order ${orderId}` });
    }
  }, [cancelExisting, refreshPortfolio]);

  const openOrders = (orderBook || []).filter(o =>
    ['open', 'pending', 'trigger pending'].includes(o.status?.toLowerCase())
  );
  const executedOrders = (orderBook || []).filter(o =>
    ['complete', 'rejected', 'cancelled'].includes(o.status?.toLowerCase())
  );

  if (connectionStatus !== 'connected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
        <p className="text-sm text-gray-400">Connect to Angel One to view orders</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {toast && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium ${
          toast.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-auto text-gray-400 hover:text-gray-600">
            <FaTimes className="text-[10px]" />
          </button>
        </div>
      )}

      {/* Error from useOrderManager */}
      {error && !toast && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-xs text-red-600 flex items-center gap-2">
          <FaExclamationTriangle />
          {error}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center" onClick={() => setCancelConfirm(null)}>
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-6 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Cancel Order?</h3>
            <p className="text-xs text-gray-500 mb-1">
              <span className="font-medium text-gray-700">{cancelConfirm.tradingsymbol}</span>
              {' '}{cancelConfirm.transactiontype} {cancelConfirm.quantity} @ {cancelConfirm.price || cancelConfirm.triggerprice || 'MKT'}
            </p>
            <p className="text-[10px] text-gray-400 mb-4">Order ID: {cancelConfirm.orderid}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setCancelConfirm(null)}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Keep Order
              </button>
              <button
                onClick={() => handleCancel(cancelConfirm)}
                disabled={loading}
                className="flex-1 px-3 py-2 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {loading ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-tabs + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {SUB_TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setSubTab(i)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                subTab === i
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab}
              {i === 0 && openOrders.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 text-[9px]">
                  {openOrders.length}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={handleRefresh}
          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <FaSync className={`text-[10px] ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Open Orders */}
      {subTab === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 bg-gray-50">
            <div className="col-span-2">Time</div>
            <div className="col-span-2">Symbol</div>
            <div className="col-span-1">Side</div>
            <div className="col-span-1">Type</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-1 text-right">Price</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-center">Actions</div>
          </div>
          {openOrders.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400">No open orders</div>
          ) : (
            openOrders.map((order, i) => (
              <div key={order.orderid || i} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 items-center text-xs">
                <div className="col-span-2 text-gray-400 text-[10px]">{order.updatetime || order.ordertime || '—'}</div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-900">{order.tradingsymbol}</span>
                </div>
                <div className="col-span-1">
                  <span className={`font-bold ${order.transactiontype === 'BUY' ? 'text-green-600' : 'text-red-600'}`}>
                    {order.transactiontype}
                  </span>
                </div>
                <div className="col-span-1 text-gray-600">{order.ordertype}</div>
                <div className="col-span-1 text-right font-mono text-gray-900">{order.quantity}</div>
                <div className="col-span-1 text-right font-mono text-gray-900">{order.price || order.triggerprice || '—'}</div>
                <div className="col-span-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 capitalize">
                    {order.status}
                  </span>
                </div>
                <div className="col-span-2 flex justify-center gap-1">
                  <button
                    onClick={() => setCancelConfirm(order)}
                    disabled={loading}
                    className="px-2.5 py-1 rounded-md text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50 transition-colors"
                    title="Cancel Order"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Executed Orders */}
      {subTab === 1 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 bg-gray-50">
            <div className="col-span-2">Time</div>
            <div className="col-span-2">Symbol</div>
            <div className="col-span-1">Side</div>
            <div className="col-span-1">Type</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-1 text-right">Price</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Avg Price</div>
          </div>
          {executedOrders.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400">No executed orders today</div>
          ) : (
            executedOrders.map((order, i) => {
              const statusColor = order.status === 'complete'
                ? 'bg-green-50 text-green-600'
                : order.status === 'rejected'
                  ? 'bg-red-50 text-red-500'
                  : 'bg-gray-50 text-gray-600';
              return (
                <div key={order.orderid || i} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 items-center text-xs">
                  <div className="col-span-2 text-gray-400 text-[10px]">{order.updatetime || '—'}</div>
                  <div className="col-span-2 font-medium text-gray-900">{order.tradingsymbol}</div>
                  <div className="col-span-1">
                    <span className={`font-bold ${order.transactiontype === 'BUY' ? 'text-green-600' : 'text-red-600'}`}>
                      {order.transactiontype}
                    </span>
                  </div>
                  <div className="col-span-1 text-gray-600">{order.ordertype}</div>
                  <div className="col-span-1 text-right font-mono text-gray-900">{order.filledshares || order.quantity}</div>
                  <div className="col-span-1 text-right font-mono text-gray-900">{order.price || '—'}</div>
                  <div className="col-span-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor} capitalize`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="col-span-2 text-right font-mono text-gray-900">
                    {order.averageprice ? `${order.averageprice}` : '—'}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* GTT Rules */}
      {subTab === 2 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 bg-gray-50">
            <div className="col-span-3">Symbol</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2 text-right">Trigger</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-center">Actions</div>
          </div>
          {gttRules.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400">No GTT rules</div>
          ) : (
            gttRules.map((rule, i) => (
              <div key={rule.id || i} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 items-center text-xs">
                <div className="col-span-3 font-medium text-gray-900">{rule.tradingsymbol}</div>
                <div className="col-span-2 text-gray-600">{rule.transactiontype}</div>
                <div className="col-span-2 text-right font-mono text-gray-900">{rule.triggerprice}</div>
                <div className="col-span-1 text-right font-mono text-gray-900">{rule.qty}</div>
                <div className="col-span-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{rule.status}</span>
                </div>
                <div className="col-span-2 flex justify-center">
                  <button
                    onClick={() => cancelGTT(rule.id, rule.symboltoken, rule.exchange)}
                    className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                  >
                    <FaTimes className="text-[10px]" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
